import React, { useState } from 'react';
import SendTextEditor from './editors/SendTextEditor';
import AskTextEditor  from './editors/AskTextEditor';
import APIEditor      from './editors/APIEditor';
import ListEditor     from './editors/ListEditor';
import MediaEditor    from './editors/MediaEditor';

const NODE_PALETTE = [
    {
        label: 'Send',
        items: [
            { type:'sendtextnode', label:'Send Text',  icon:'bi-chat-text',   color:'#1565c0' },
            { type:'imagenode',    label:'Send Media', icon:'bi-image',       color:'#0891b2' },
        ],
    },
    {
        label: 'Ask',
        items: [
            { type:'asktextnode', label:'Ask Text',     icon:'bi-question-circle', color:'#1565c0' },
            { type:'numbernode',  label:'Ask Number',   icon:'bi-hash',            color:'#16a34a' },
            { type:'phonenode',   label:'Ask Phone',    icon:'bi-telephone',       color:'#0891b2' },
            { type:'emailnode',   label:'Ask Email',    icon:'bi-envelope',        color:'#d97706' },
            { type:'datenode',    label:'Ask Date',     icon:'bi-calendar3',       color:'#ec4899' },
            { type:'locnode',     label:'Ask Location', icon:'bi-geo-alt',         color:'#dc2626' },
            { type:'urlnode',     label:'Ask URL',      icon:'bi-link-45deg',      color:'#f97316' },
            { type:'filenode',    label:'Ask File',     icon:'bi-file-earmark',    color:'#7c3aed' },
            { type:'listnode',    label:'List Menu',    icon:'bi-list-ul',         color:'#7c3aed' },
        ],
    },
    {
        label: 'Connectors',
        items: [
            { type:'apinode', label:'API Call', icon:'bi-globe', color:'#7c3aed' },
        ],
    },
];

const ASK_TYPES = new Set(['asktextnode','phonenode','emailnode','datenode','numbernode','locnode','urlnode','filenode']);

export default function FlowSidebar({
    onDone, selectedNode, setSelectedElements,
    nodeAskText, setNodeAskText,
    nodesendText, setNodeSendText,
    nodeImg, setNodeImg,
    mediaType, setMediaType,
    mediaCaption, setMediaCaption,
    nodeList, setNodeList,
    nodeAPI, setNodeAPI,
    deleteSelectedNode,
}) {
    const [search,   setSearch]   = useState('');
    const [expanded, setExpanded] = useState({ Send:true, Ask:true, Connectors:true });

    const toggle = (label) => setExpanded(p => ({ ...p, [label]: !p[label] }));

    const onDragStart = (e, type) => {
        e.dataTransfer.setData('application/reactflow', type);
        e.dataTransfer.effectAllowed = 'move';
    };

    const renderEditor = () => {
        if (!selectedNode) return null;
        const t = selectedNode.type;
        if (t === 'sendtextnode') return <SendTextEditor value={nodesendText} onChange={setNodeSendText}/>;
        if (t === 'imagenode') return <MediaEditor url={nodeImg} setUrl={setNodeImg} type={mediaType} setType={setMediaType} caption={mediaCaption} setCaption={setMediaCaption}/>;
        if (ASK_TYPES.has(t)) return <AskTextEditor value={nodeAskText} onChange={setNodeAskText} nodeType={t}/>;
        if (t === 'listnode') return <ListEditor value={nodeList} onChange={setNodeList} nodeData={selectedNode.data}/>;
        if (t === 'apinode') return <APIEditor value={nodeAPI} onChange={setNodeAPI}/>;
        return <div style={{ padding:16, color:'#9ca3af', fontSize:13, textAlign:'center' }}>No editor for this node type.</div>;
    };

    const filtered = (items) => items.filter(i =>
        !search || i.label.toLowerCase().includes(search.toLowerCase()) || i.type.includes(search.toLowerCase())
    );

    return (
        <div style={{ width:260, height:'100%', background:'#fff', borderLeft:'1px solid #e5e7eb', display:'flex', flexDirection:'column', overflow:'hidden' }}>
            {selectedNode ? (
                <div style={{ flex:1, overflowY:'auto', display:'flex', flexDirection:'column' }}>
                    {/* Node header */}
                    <div style={{ padding:'12px 16px', borderBottom:'1px solid #e5e7eb', background:'#f8fafc', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
                        <div>
                            <p style={{ fontSize:12, fontWeight:700, color:'#111827', margin:0 }}>{selectedNode.type}</p>
                            <p style={{ fontSize:10, color:'#9ca3af', fontFamily:'monospace', margin:0 }}>{selectedNode.id}</p>
                        </div>
                        <div style={{ display:'flex', gap:6 }}>
                            <button onClick={deleteSelectedNode}
                                style={{ padding:'5px 10px', fontSize:11, fontWeight:600, color:'#dc2626', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:6, cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
                                <i className="bi bi-trash" style={{ fontSize:11 }}></i> Delete
                            </button>
                            <button onClick={() => { if (onDone) onDone(); setSelectedElements([]); }}
                                style={{ padding:'5px 10px', fontSize:11, color:'#374151', background:'#f3f4f6', border:'1px solid #e5e7eb', borderRadius:6, cursor:'pointer' }}>
                                Done
                            </button>
                        </div>
                    </div>
                    <div style={{ flex:1, overflowY:'auto' }}>
                        {renderEditor()}
                    </div>
                </div>
            ) : (
                <div style={{ flex:1, overflowY:'auto', display:'flex', flexDirection:'column' }}>
                    {/* Search */}
                    <div style={{ padding:'10px 12px', borderBottom:'1px solid #e5e7eb', flexShrink:0 }}>
                        <div style={{ position:'relative' }}>
                            <i className="bi bi-search" style={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)', color:'#9ca3af', fontSize:12 }}></i>
                            <input value={search} onChange={e => setSearch(e.target.value)}
                                placeholder="Search nodes..."
                                style={{ width:'100%', paddingLeft:28, paddingRight:8, paddingTop:7, paddingBottom:7, border:'1px solid #e5e7eb', borderRadius:8, fontSize:12, color:'#111827', outline:'none', boxSizing:'border-box' }}/>
                        </div>
                    </div>

                    <div style={{ padding:'8px 12px', flex:1, overflowY:'auto' }}>
                        {NODE_PALETTE.map(group => {
                            const groupItems = filtered(group.items);
                            if (groupItems.length === 0) return null;
                            return (
                                <div key={group.label} style={{ marginBottom:12 }}>
                                    <button onClick={() => toggle(group.label)}
                                        style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', padding:'4px 0', background:'none', border:'none', cursor:'pointer', marginBottom:6 }}>
                                        <span style={{ fontSize:10, fontWeight:700, color:'#9ca3af', letterSpacing:'0.1em', textTransform:'uppercase' }}>{group.label}</span>
                                        <i className={`bi bi-chevron-${expanded[group.label] ? 'up' : 'down'}`} style={{ fontSize:10, color:'#9ca3af' }}></i>
                                    </button>

                                    {expanded[group.label] && (
                                        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                                            {groupItems.map(item => (
                                                <div key={item.type} draggable onDragStart={e => onDragStart(e, item.type)}
                                                    style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 10px', background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:8, cursor:'grab', transition:'all 0.15s', userSelect:'none' }}
                                                    onMouseEnter={e => { e.currentTarget.style.background='#eff6ff'; e.currentTarget.style.borderColor='#bfdbfe'; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background='#f8fafc'; e.currentTarget.style.borderColor='#e5e7eb'; }}>
                                                    <div style={{ width:28, height:28, borderRadius:6, background:item.color+'18', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                                                        <i className={`bi ${item.icon}`} style={{ fontSize:13, color:item.color }}></i>
                                                    </div>
                                                    <div>
                                                        <p style={{ fontSize:12, fontWeight:600, color:'#111827', lineHeight:1.2, margin:0 }}>{item.label}</p>
                                                        <p style={{ fontSize:10, color:'#9ca3af', fontFamily:'monospace', margin:0 }}>{item.type}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
